import { isMediaInputDevice } from '@yandex-360/telemost-sdk/device-manager';
import { MediaStreamKind, ParticipantSlot, SlotType } from '@yandex-video-platform/goloom-sdk';

import { ImageName } from '../../../shared/components/svg-image/images';
import { SELF } from '../../../shared/constants';
import { deviceManager, goloomSdk } from '../../../shared/features/goloom-sdk';
import { createLogger } from '../../../shared/helpers/loggerFacade';
import { NetworkQualityType } from '../constants';
import { Participant, ParticipantDrawerParams, StreamToVideosMap } from '../types';
import { getNetworkQualityTypeByScore } from './get-network-quality-type-by-score';
import { isVideoReadyForDraw } from './is-video-ready-for-draw';
import { hasVideoLimitation } from './video-limitation';

const logger = createLogger('record-sagas-goloom-helpers');
const desktopTrackToRecordVideoMap = new WeakMap<MediaStreamTrack, HTMLVideoElement>();

export const resolveVideoByTrack = (
    track: Maybe<MediaStreamTrack | null>,
    streamToVideosMap: StreamToVideosMap
): Maybe<HTMLVideoElement> => {
    if (!track) {
        return undefined;
    }

    const videoByTrack = streamToVideosMap.byTrack.get(track);

    if (videoByTrack) {
        return videoByTrack;
    }

    if (!track.id) {
        return undefined;
    }

    return streamToVideosMap.byTrackId.get(track.id);
};

export const getOrCreateDesktopRecordVideo = (track: Maybe<MediaStreamTrack | null>): Maybe<HTMLVideoElement> => {
    if (!track) {
        return undefined;
    }

    const cachedVideo = desktopTrackToRecordVideoMap.get(track);
    if (cachedVideo) {
        return cachedVideo;
    }

    try {
        const recordVideo = document.createElement('video');
        recordVideo.autoplay = true;
        recordVideo.muted = true;
        recordVideo.playsInline = true;
        recordVideo.srcObject = new MediaStream([track]);

        const playPromise = recordVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch((error) => {
                logger.warn(error, {
                    method: 'getOrCreateDesktopRecordVideo',
                    additional: {
                        trackId: track.id,
                    },
                });
            });
        }

        track.addEventListener(
            'ended',
            () => {
                const mapVideo = desktopTrackToRecordVideoMap.get(track);
                if (mapVideo === recordVideo) {
                    mapVideo.srcObject = null;
                    desktopTrackToRecordVideoMap.delete(track);
                }
            },
            { once: true }
        );

        desktopTrackToRecordVideoMap.set(track, recordVideo);
        return recordVideo;
    } catch {
        return undefined;
    }
};

export const resolveDesktopRecordingVideo = (
    track: Maybe<MediaStreamTrack | null>,
    streamToVideosMap: StreamToVideosMap
): Maybe<HTMLVideoElement> => {
    const domVideo = resolveVideoByTrack(track, streamToVideosMap);
    const recorderVideo = getOrCreateDesktopRecordVideo(track);

    if (isVideoReadyForDraw(recorderVideo)) {
        return recorderVideo;
    }

    if (isVideoReadyForDraw(domVideo)) {
        return domVideo;
    }

    return recorderVideo || domVideo;
};

export const getAudioStreamTracks = (): MediaStreamTrack[] => {
    const audioStreamTracks: MediaStreamTrack[] = goloomSdk.session?.audioTracks || [];

    if (goloomSdk.session?.audioStatus) {
        const myAudioInputDevice = deviceManager.audioInputActiveDevice;

        if (myAudioInputDevice && isMediaInputDevice(myAudioInputDevice) && myAudioInputDevice.track) {
            audioStreamTracks.push(myAudioInputDevice.track);
        }
    }

    if (goloomSdk.session?.activeDisplayMedia?.audio) {
        audioStreamTracks.push(goloomSdk.session.activeDisplayMedia.audio);
    }

    return audioStreamTracks;
};

export const getParticipantDrawerParams = (
    participant: Participant,
    streamToVideosMap: StreamToVideosMap,
    isDrawDesktopTrack: boolean,
    isDrawPresenter: boolean
): ParticipantDrawerParams => {
    const isDesktopTrack = isDrawPresenter && participant.desktopTrackState && !participant.desktopTrackState.muted;
    const isSelf = participant.id === SELF;
    const goloomParticipants = goloomSdk.session?.participants;
    const goloomParticipant = isSelf
        ? goloomSdk.getMe()
        : goloomParticipants?.find(({ userId }) => userId === participant.id);

    if (!goloomParticipant) {
        return {
            isDesktopTrack,
            video: undefined,
            hasFrameBorder: undefined,
        };
    }

    const participantSlot = ((): Maybe<Partial<ParticipantSlot>> => {
        if (isDesktopTrack && isSelf) {
            return {
                label: '',
                vad: null,
                track: goloomSdk.session?.activeDisplayMedia?.video ?? null,
                participant: goloomParticipant,
                mediaStreamKind: MediaStreamKind.DISPLAY,
                pinned: true,
            };
        }

        const slots = goloomSdk.session?.slots.current.filter(
            ({ type }) => type === SlotType.PARTICIPANT
        ) as ParticipantSlot[];
        const streamKind = isDesktopTrack ? MediaStreamKind.DISPLAY : MediaStreamKind.DEVICES;
        return slots?.find(
            ({ participant: participantFromSlot, mediaStreamKind }) =>
                participantFromSlot?.userId === goloomParticipant.userId && mediaStreamKind === streamKind
        );
    })();

    const isMuted = participant.desktopTrackState?.muted && participant.videoTrackState?.muted;
    const hasFrameBorder = isSelf
        ? Boolean(goloomSdk.session?.audioStatus) && goloomSdk.getLocalVad()
        : Boolean(participantSlot?.vad);

    /* Не рисуем:
    - замьюченное видео;
    - отсутствие трека;
    - шаринг (если передан специальный флаг)
     */
    let video =
        isMuted || !participantSlot?.track || (!isDrawDesktopTrack && isDesktopTrack)
            ? undefined
            : isDesktopTrack
              ? resolveDesktopRecordingVideo(participantSlot.track, streamToVideosMap)
              : resolveVideoByTrack(participantSlot.track, streamToVideosMap);

    if (!isMuted && !(!isDrawDesktopTrack && isDesktopTrack) && !video && isSelf) {
        if (isDesktopTrack) {
            video = resolveDesktopRecordingVideo(goloomSdk.session?.activeDisplayMedia?.video, streamToVideosMap);
        } else {
            const device = deviceManager.videoInputActiveDevice;

            if (device && isMediaInputDevice(device) && device.track) {
                video = resolveVideoByTrack(device.track, streamToVideosMap);
            }
        }
    }

    /**
     * Получаем статусы для слота участника/демонстрации
     */
    const statusIcons: ImageName[] = [];
    const networkQualityScore = isSelf ? goloomSdk.getNetworkScore() : participantSlot?.participant?.networkScore;
    const videoLimitation = isSelf ? undefined : participantSlot?.limitationReason;

    if (networkQualityScore && getNetworkQualityTypeByScore(networkQualityScore) === NetworkQualityType.BAD) {
        statusIcons.push('network');
    }

    if (videoLimitation && hasVideoLimitation(videoLimitation)) {
        statusIcons.push('cameraOffWithSpace');
    }

    return {
        isDesktopTrack,
        video,
        hasFrameBorder,
        statusIcons,
    };
};

export const canStartRecording = (): boolean => {
    return Boolean(goloomSdk.session);
};
